import { Images, Pencil, Plus, RotateCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { OfflineImage } from "@/components/common/offline-image/offline-image";

/**
 * One photo tile in the grid. `assetKey` feeds the offline asset cache
 * ([[offline-image.spec]]); `src` is the live thumbnail URL fallback.
 */
export interface RecordPhotoTile {
  /** Stable key for React + the viewer index mapping. */
  id: string;
  /** Offline-cache key (content-versioned). Empty string disables cache lookup. */
  assetKey: string;
  /** Live thumbnail URL — used online and as the cache fallback. */
  src: string;
  alt?: string;
  /**
   * Marks an optimistic [[append-photo-outbox]] tile. `pending` tiles render as
   * ORDINARY tiles (no syncing badge — append sync is surfaced on the record's
   * grid tile, not here). A `failed` tile shows a "tap to retry" affordance and
   * its tap fires `onRetry(index)` instead of `onOpen`. Optimistic tiles are
   * never deletable, so they carry no delete "×" regardless of `editable`.
   */
  pendingAppend?: "pending" | "failed";
}

export interface RecordPhotoGridProps {
  photos: RecordPhotoTile[];
  /**
   * Edit affordances. When true, each tile shows a delete "×". The single-
   * photo-delete-maps-to-record-delete rule lives in the parent
   * ([[image-viewer-modal]]); this component only reports the per-index remove
   * intent.
   */
  editable?: boolean;
  /**
   * Whether to render the "+ Add" tile. Present in normal authenticated view /
   * edit / new modes (the design), but MUST be hidden on the read-only public
   * share page (`viewOnly`) — an unauthenticated visitor must never see a write
   * affordance. The parent passes `!viewOnly`. Defaults to true so the common
   * authenticated case needs no opt-in.
   */
  canAdd?: boolean;
  /** Tap a tile (not its delete button) → open the fullscreen viewer at `index`. */
  onOpen: (index: number) => void;
  /** Tap the "+ Add" tile. Only invoked when `canAdd` is true. */
  onAdd: () => void;
  /** Tap a tile's delete "×" (editable only). */
  onRemove?: (index: number) => void;
  /**
   * Tap a tile's "Edit markups" pencil. Rendered only in editable mode AND when
   * this handler is supplied. The write path — new batch / queued draft /
   * synced record — is owned by the parent ([[image-viewer-modal]]); this
   * component only reports the per-index intent, exactly like `onRemove`.
   * Optimistic append tiles never carry a pencil (mirrors the delete "×").
   */
  onEditMarkups?: (index: number) => void;
  /**
   * Tap a `pendingAppend === "failed"` tile → re-queue that append for sync
   * ([[append-photo-outbox]]). Replaces `onOpen` for failed optimistic tiles.
   */
  onRetry?: (index: number) => void;
  /**
   * Who uploaded the record, rendered on the count row as "· Uploaded by N".
   * It used to be its own line in the details column; on the count row it costs
   * no vertical space and sits beside the photos it describes. Omitted for a
   * new capture and for a record with no creator identity — the host decides.
   */
  uploadedBy?: string;
  /**
   * When the record's most recent photo was taken, already formatted for
   * display. Rendered as "Latest photo {value}" on its own line ABOVE the
   * count row — it is the first thing someone checks when a photo's age decides
   * whether it still describes the site. Omitted for a new capture (nothing is
   * recorded yet) and whenever the host cannot resolve a date.
   */
  latestPhotoAt?: string;
  className?: string;
}

/**
 * The always-3-column thumbnail grid of a record's photos. A single-photo
 * record and a many-photo record render identically — only the tile count
 * differs. See [[record-photo-grid]].
 */
export function RecordPhotoGrid({
  photos,
  editable = false,
  canAdd = true,
  onOpen,
  onAdd,
  onRemove,
  onEditMarkups,
  onRetry,
  uploadedBy,
  latestPhotoAt,
  className,
}: RecordPhotoGridProps) {
  const count = photos.length;
  // Tiles the grid will actually lay out: the photos plus the "+ Add" cell.
  const tileCount = count + (canAdd ? 1 : 0);
  // The first photo spans 2x2, so the two cells of the third column beside it
  // must be filled or the hero leaves a hole. Three tiles fill them outright;
  // with exactly two, the second can only fill the column if it is "+ Add",
  // which stretches (a photo would be cropped to a 1:2 sliver instead). Below
  // that the grid stays flat squares, as it always was. See
  // [[record-photo-grid.spec]] § Layout.
  const heroFirst = count > 0 && (tileCount >= 3 || (tileCount === 2 && canAdd));
  const stretchAddTile = heroFirst && tileCount === 2;
  return (
    <div className={cn("space-y-2.5", className)} data-slot="record-photo-grid">
      <div className="space-y-1 px-1">
        {latestPhotoAt ? (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Latest photo</span> {latestPhotoAt}
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Images className="h-3.5 w-3.5 shrink-0" />
            <span className="whitespace-nowrap">
              {count} {count === 1 ? "photo" : "photos"}
            </span>
            {uploadedBy ? (
              // The uploader truncates, the count never does: which photo set
              // this is outranks whose it is when the column is narrow. The
              // separator is its own node so the uploader reads as one string.
              <>
                <span aria-hidden="true">&middot;</span>
                <span className="truncate font-normal">
                  Uploaded by {uploadedBy}
                </span>
              </>
            ) : null}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1">
        {photos.map((photo, index) => {
          const isOptimistic = photo.pendingAppend != null;
          const isFailed = photo.pendingAppend === "failed";
          // A failed append re-queues on tap (tap-to-retry lives on the failed
          // photo per [[append-photo-outbox]]); every other tile opens the
          // viewer. Optimistic tiles are never deletable, so they carry no "×".
          const handleActivate = () =>
            isFailed ? onRetry?.(index) : onOpen(index);
          return (
            <div
              key={photo.id}
              className={cn(
                "group relative aspect-square cursor-pointer overflow-hidden rounded bg-muted",
                // The hero keeps `aspect-square`: two columns plus the gap
                // between them is exactly the height of two rows plus theirs,
                // so the block stays square and the rows line up with the
                // 1x1 tiles in the third column.
                heroFirst && index === 0 && "col-span-2 row-span-2",
              )}
              role="button"
              tabIndex={0}
              aria-label={
                isFailed
                  ? `Retry photo ${index + 1}`
                  : `Open photo ${index + 1} of ${count}`
              }
              onClick={handleActivate}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleActivate();
                }
              }}
            >
              <OfflineImage
                assetKey={photo.assetKey}
                src={photo.src}
                alt={photo.alt ?? `Photo ${index + 1}`}
                draggable={false}
                className="h-full w-full object-cover transition-transform duration-200 group-active:scale-[0.97]"
              />
              {/* Failed append: a tap-to-retry overlay. Pending optimistic tiles
                  stay bare — append sync is surfaced on the record's grid tile,
                  not inside the open viewer. */}
              {isFailed && (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 text-white">
                  <RotateCcw className="h-5 w-5" />
                  <span className="text-[11px] font-medium">Tap to retry</span>
                </div>
              )}
              {editable && onEditMarkups && !isOptimistic && (
                <button
                  type="button"
                  aria-label={`Edit markups on photo ${index + 1}`}
                  className="absolute left-1 top-1 z-[2] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditMarkups(index);
                  }}
                >
                  <Pencil className="h-[12px] w-[12px]" />
                </button>
              )}
              {editable && onRemove && !isOptimistic && (
                <button
                  type="button"
                  aria-label={`Remove photo ${index + 1}`}
                  className="absolute right-1 top-1 z-[2] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(index);
                  }}
                >
                  <X className="h-[13px] w-[13px]" />
                </button>
              )}
            </div>
          );
        })}

        {/* "+ Add" tile — present in authenticated view / edit / new modes,
            hidden on the read-only public share page (`canAdd === false`) so an
            unauthenticated visitor never sees a write affordance. */}
        {canAdd && (
          <button
            type="button"
            aria-label="Add photo"
            onClick={onAdd}
            className={cn(
              "flex aspect-square flex-col items-center justify-center gap-1 rounded border border-dashed border-muted-foreground/45 bg-muted/50 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
              // Sole companion to the hero: fill the third column rather than
              // leaving the cell below it empty. The dashed box has no content
              // to distort, so it is the one tile that can take the stretch.
              stretchAddTile && "aspect-auto row-span-2 h-full",
            )}
          >
            <Plus className="h-5 w-5" />
            Add
          </button>
        )}
      </div>
    </div>
  );
}
